import React, { useCallback, useEffect } from 'react'
import { Offcanvas } from 'react-bootstrap'
import useShowRatePlan from '../../../services/rate-plan/hooks/useShowRatePlan'
import FormError from '../../../shared/components/errors/FormError'
import FormSpinner from '../../../shared/components/spinner/FormSpinner'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SpecificRule } from '../../../shared/types/rate/SpecificRule'
import { SpecificAmount } from '../../../shared/types/rate/SpecificAmountType'
import CurrencyInput from '../../../shared/components/form/CurrencyInput'
import clsx from 'clsx'
import Select from 'react-select'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { UpdateLinkRateForm, UpdateLinkRateSchema } from '../../../services/rate-plan/schema/UpdateLinkRateSchema'
import { useUpdateLinkRatePlan } from '../../../services/rate-plan/hooks/useUpdateLinkRatePlan'
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation'

interface UpdateLinkRateDialogProps {
    show: boolean
    onClose: () => void,
    rateId: string
}

const UpdateLinkRateDialog: React.FC<UpdateLinkRateDialogProps> = ({ show, onClose, rateId }) => {
    const { data: linkRate, isFetching: isFetchingLinkRate, isError: isLinkRateError } = useShowRatePlan({
        id: rateId,
    })

    const { data: parentRate, isFetching: isFetchingParentRate, isError: isParentRateError } = useShowRatePlan({
        id: linkRate?.data.parent_id ?? undefined
    })

    const { register, handleSubmit, formState: { errors }, control, watch, reset, setError } = useForm<UpdateLinkRateForm>({
        resolver: zodResolver(UpdateLinkRateSchema)
    });

    useEffect(() => {
        if (linkRate && parentRate) {
            reset({
                name: linkRate.data.name || '',
                description: linkRate.data.description || '',
                max_night: linkRate.data.max_night || 0,
                min_night: linkRate.data.min_night || 0,
                guest_include: linkRate.data.guest_include || 0,
                guest_max: linkRate.data.guest_max || 0,
                extra_adult: linkRate.data.extra_adult || 0,
                extra_child: linkRate.data.extra_child || 0,
                extra_adult_rate: linkRate.data.extra_adult_rate || 0,
                extra_child_rate: linkRate.data.extra_child_rate || 0,
                specific_rule: SpecificRule.find((val) => val.value === linkRate.data.specific_rule)?.value || '',
                specific_amount_type: SpecificAmount.find((val) => val.value === linkRate.data.specific_amount_type)?.value || '',
                specific_value: linkRate.data.specific_value || 0,
                inherit_restriction: linkRate.data?.inherit_restriction,
                inherit_closed_to_arrival: Boolean(linkRate.data?.inherit_closed_to_arrival),
                inherit_closed_to_departure: Boolean(linkRate.data?.inherit_closed_to_departure),
                inherit_stop_sell: Boolean(linkRate.data?.inherit_stop_sell),
                inherit_min_stay_arrival: Boolean(linkRate.data?.inherit_min_stay_arrival),
                inherit_min_stay_through: Boolean(linkRate.data?.inherit_min_stay_through),
                inherit_max_stay: Boolean(linkRate.data?.inherit_max_stay),
            })
        }
    }, [linkRate, parentRate])

    const inheritRestriction = watch('inherit_restriction');

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useUpdateLinkRatePlan();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateLinkRateForm) => {
        toast.loading('Updating Link Rate...', { id: 'update-link-rate' });

        mutate({ rateId: rateId, formData: data }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates'].includes(key as string))
                });

                toast.success('Link Rate updated successfully', { id: 'update-link-rate' });
            },
            onError: (mutationError: any) => {
                const handled = applyServerValidationErrors(mutationError, setError, {
                    toastId: 'update-link-rate',
                });

                if (!handled) {
                    toast.error(mutationError?.response?.data?.message || 'Failed to update link rate', { id: 'update-link-rate' });
                }
            },
        });
    }, [mutate, rateId]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Update Link Rate</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {(isLinkRateError || isParentRateError) && (
                    <FormError />
                )}
                {(isFetchingLinkRate || isFetchingParentRate) && (
                    <FormSpinner />
                )}
                {(linkRate && parentRate && !isFetchingLinkRate && !isFetchingParentRate) && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className='row mb-3 align-items-center'>
                            <label htmlFor='rate_id' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Rate Parent: </label>
                            <div className='col-sm-8'>
                                <span className='badge bg-primary text-white rounded-1'>{parentRate.data.name}</span>
                            </div>
                        </div>

                        <div className='row mb-3 align-items-center'>
                            <label htmlFor='inherit_restriction' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Inherit Restriction: </label>
                            <div className='col-sm-8'>
                                <div className='form-check form-switch'>
                                    <Controller
                                        name='inherit_restriction'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type='checkbox'
                                                className='form-check-input'
                                                checked={field.value ? true : false}
                                                onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                ref={field.ref}
                                                value={field.value ? 1 : 0}
                                            />
                                        )}
                                    />
                                    <label className='form-check-label' htmlFor='inherit_restriction'></label>
                                    {errors.inherit_restriction && (
                                        <div className='invalid-feedback'>{typeof errors.inherit_restriction.message === 'string' && errors.inherit_restriction.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Link Rate Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder='Enter rate name'
                                    {...register('name', { required: 'Rate name is required' })}
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">{typeof errors.name.message === 'string' && errors.name.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Description: </label>
                            <div className="col-sm-8">
                                <textarea
                                    className={clsx("form-control app-form-control rounded-1", errors?.description && 'is-invalid')}
                                    id="description"
                                    placeholder='Enter rate description'
                                    {...register('description')}
                                />
                                {errors.description && (
                                    <div className="invalid-feedback">{typeof errors.description.message === 'string' && errors.description.message}</div>
                                )}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Rate Settings</h3>
                        {/* Max Night */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="max_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Max Night: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.max_night && 'is-invalid')}
                                    id="max_night"
                                    placeholder='Enter max night'
                                    {...register('max_night', { required: 'Max night is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.max_night && (
                                    <div className="invalid-feedback">{typeof errors.max_night.message === 'string' && errors.max_night.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Min Night */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="min_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Night: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.min_night && 'is-invalid')}
                                    id="min_night"
                                    placeholder='Enter min night'
                                    {...register('min_night', { required: 'Min night is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.min_night && (
                                    <div className="invalid-feedback">{typeof errors.min_night.message === 'string' && errors.min_night.message}</div>
                                )}
                            </div>
                        </div>

                        {/* guest_include */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="guest_include" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Include: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.guest_include && 'is-invalid')}
                                    id="guest_include"
                                    placeholder='Enter guest include'
                                    {...register('guest_include', { required: 'Guest include is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.guest_include && (
                                    <div className="invalid-feedback">{typeof errors.guest_include.message === 'string' && errors.guest_include.message}</div>
                                )}
                            </div>
                        </div>

                        {/* guest_max */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="guest_max" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Max: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.guest_max && 'is-invalid')}
                                    id="guest_max"
                                    placeholder='Enter guest max'
                                    {...register('guest_max', { required: 'Guest max is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.guest_max && (
                                    <div className="invalid-feedback">{typeof errors.guest_max.message === 'string' && errors.guest_max.message}</div>
                                )}
                            </div>
                        </div>

                        {/* extra_adult */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_adult" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.extra_adult && 'is-invalid')}
                                    id="extra_adult"
                                    placeholder='Enter extra adult'
                                    {...register('extra_adult', { required: 'Extra adult is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.extra_adult && (
                                    <div className="invalid-feedback">{typeof errors.extra_adult.message === 'string' && errors.extra_adult.message}</div>
                                )}
                            </div>
                        </div>

                        {/* extra_child */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_child" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.extra_child && 'is-invalid')}
                                    id="extra_child"
                                    placeholder='Enter extra child'
                                    {...register('extra_child', { required: 'Extra child is required' })}
                                    disabled={inheritRestriction ? true : false}
                                />
                                {errors.extra_child && (
                                    <div className="invalid-feedback">{typeof errors.extra_child.message === 'string' && errors.extra_child.message}</div>
                                )}
                            </div>
                        </div>

                        {watch('inherit_restriction') !== 1 && (
                            <>
                                <h3 className="mt-6 border-bottom fw-normal">Restrictions Settings</h3>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_closed_to_arrival' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Click Through Arival: </label>
                                    <div className='col-sm-8'>
                                        {/* Checkbox */}
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_closed_to_arrival'
                                                {...register('inherit_closed_to_arrival')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_closed_to_arrival'></label>
                                        </div>
                                    </div>
                                </div>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_closed_to_departure' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Click Through Departure: </label>
                                    <div className='col-sm-8'>
                                        {/* Checkbox */}
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_closed_to_departure'
                                                {...register('inherit_closed_to_departure')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_closed_to_departure'></label>
                                        </div>
                                    </div>
                                </div>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_stop_sell' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Stop Sell: </label>
                                    <div className='col-sm-8'>
                                        {/* Checkbox */}
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_stop_sell'
                                                {...register('inherit_stop_sell')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_stop_sell'></label>
                                        </div>
                                    </div>
                                </div>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_min_stay_arrival' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Min Stay Arrival: </label>
                                    <div className='col-sm-8'>
                                        {/* Checkbox */}
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_min_stay_arrival'
                                                {...register('inherit_min_stay_arrival')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_min_stay_arrival'></label>
                                        </div>
                                    </div>
                                </div>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_min_stay_through' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Min Stay Through: </label>
                                    <div className='col-sm-8'>
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_min_stay_through'
                                                {...register('inherit_min_stay_through')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_min_stay_through'></label>
                                        </div>
                                    </div>
                                </div>
                                <div className='row mb-3 align-items-center'>
                                    <label htmlFor='inherit_max_stay' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Max Stay: </label>
                                    <div className='col-sm-8'>
                                        <div className='form-check form-switch'>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                id='inherit_max_stay'
                                                {...register('inherit_max_stay')}
                                            />
                                            <label className='form-check-label' htmlFor='inherit_max_stay'></label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <h3 className="mt-6 border-bottom fw-normal">Rate Price Settings</h3>
                        {/* extra_adult_rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_adult_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="extra_adult_rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Extra adult rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Extra adult rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value ?? 0} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.extra_adult_rate?.message === 'string' ? errors.extra_adult_rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter extra adult rate in Rupiah"
                                            disabled={inheritRestriction ? true : false} // Nonaktifkan jika inherit restriction aktif
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* extra_child_rate */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="extra_child_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child Rate: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="extra_child_rate" // Nama field yang diatur oleh React Hook Form
                                    control={control}
                                    rules={{
                                        required: "Extra child rate is required", // Validasi required
                                        min: {
                                            value: 1000,
                                            message: "Extra child rate must be at least Rp. 1,000",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field} // Menghubungkan props ke CurrencyInput
                                            value={field.value ?? 0} // Nilai dari React Hook Form
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }} // Ambil angka murni dari CurrencyInput
                                            error={typeof errors.extra_child_rate?.message === 'string' ? errors.extra_child_rate.message : undefined} // Tampilkan pesan error jika ada
                                            placeholder="Enter extra child rate in Rupiah"
                                            disabled={inheritRestriction ? true : false} // Nonaktifkan jika inherit restriction aktif
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Link Rate Settings</h3>
                        <div className='row mb-3 align-items-center'>
                            <label htmlFor='specific_rule' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Rule: </label>
                            <div className='col-sm-8'>
                                <Controller
                                    name="specific_rule"
                                    control={control}
                                    rules={{
                                        required: "Specific rule is required",
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={SpecificRule}
                                            className={clsx("app-form-control", errors.specific_rule && "is-invalid")}
                                            placeholder="Select specific rule"
                                            onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                            value={SpecificRule.find((option) => option.value === field.value)}
                                        />
                                    )}
                                />
                                {errors.specific_rule && (
                                    <div className="invalid-feedback d-block">{typeof errors.specific_rule.message === 'string' && errors.specific_rule.message}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mb-3 align-items-center'>
                            <label htmlFor='specific_amount_type' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Amount Type: </label>
                            <div className='col-sm-8'>
                                <Controller
                                    name='specific_amount_type'
                                    control={control}
                                    rules={{
                                        required: 'Specific amount type is required'
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={SpecificAmount}
                                            className={clsx('app-form-control', errors.specific_amount_type && 'is-invalid')}
                                            placeholder='Select specific amount type'
                                            onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                            value={SpecificAmount.find((option) => option.value === field.value)}
                                        />
                                    )}
                                />
                                {errors.specific_amount_type && (
                                    <div className="invalid-feedback d-block">{typeof errors.specific_amount_type.message === 'string' && errors.specific_amount_type.message}</div>
                                )}
                            </div>
                        </div>

                        <div className='row mb-3 align-items-center'>
                            <label htmlFor='specific_value' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Value: </label>
                            <div className='col-sm-8'>
                                {watch('specific_amount_type') === 'percent' ? (
                                    <input
                                        type='number'
                                        className={clsx('form-control app-form-control rounded-1', errors.specific_value && 'is-invalid')}
                                        id='specific_value'
                                        placeholder='Enter specific value'
                                        {...register('specific_value', { required: 'Specific value is required' })}
                                    />
                                ) : (
                                    <Controller
                                        name='specific_value'
                                        control={control}
                                        rules={{
                                            required: 'Specific value is required'
                                        }}
                                        render={({ field }) => (
                                            <CurrencyInput
                                                {...field}
                                                value={field.value}
                                                onChange={(e, rawValue) => {
                                                    e.preventDefault();
                                                    field.onChange(rawValue);
                                                }}
                                                error={typeof errors.specific_value?.message === 'string' ? errors.specific_value.message : undefined}
                                                placeholder='Enter specific value in Rupiah'
                                            />
                                        )}
                                    />
                                )}
                                {errors.specific_value && (
                                    <div className='invalid-feedback'>{typeof errors.specific_value.message === 'string' && errors.specific_value.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group mt-auto d-grid">
                            <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                                {isPending ? 'Updating...' : 'Update Link Rate'}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default UpdateLinkRateDialog